import { queryClient } from '@/api/queryClient';
import { authAdapter } from '@/auth';
import { AppShell } from '@/components/shell/AppShell';
import { RouteError, RouteNotFound } from '@/components/shell/RouteBoundaries';
import { Toaster } from '@/components/ui/sonner';
import type { RouterContext } from '@/router';
/**
 * Root route — the single shell every route renders inside (frontend-map §1.1).
 * FOUNDATION-owned.
 *
 * Provider order (outermost → innermost):
 *   1. Auth Provider            — the active auth adapter (Clerk, or the dev bypass)
 *   2. <QueryClientProvider>    — server cache (the shared queryClient)
 *   3. Auth Gate                — clerk: signed-out → <SignIn/>, signed-in → shell;
 *                                 dev: renders the shell directly (no sign-in wall)
 *   4. <AppShell> + <AppNav>    — persistent chrome
 *   5. <Outlet/>                — the active route
 *   6. <Toaster/>               — mounted once for mutation feedback
 *
 * The auth seam lives in `@/auth` (VITE_AUTH_MODE selects clerk vs dev). Auth +
 * Query are the ONLY React providers (frontend-map §0). All app state is Zustand.
 * `next-themes` ThemeProvider is included solely so the sonner Toaster can resolve
 * a theme; it is a library shell, not app state.
 */
import { QueryClientProvider } from '@tanstack/react-query';
import { Outlet, createRootRouteWithContext } from '@tanstack/react-router';
import { ThemeProvider } from 'next-themes';

const { Provider: AuthProvider, Gate: AuthGate } = authAdapter;

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
});

function RootComponent() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
          <AuthGate>
            <AppShell>
              <Outlet />
            </AppShell>
          </AuthGate>
          <Toaster richColors closeButton />
        </ThemeProvider>
      </QueryClientProvider>
    </AuthProvider>
  );
}
